import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Button, FloatingLabelInput, Link, Message, Typography } from "@aviary";
import { isEmailValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { useUnauthUrlFields } from "@shared/hooks/useUnauthUrlFields/useUnauthUrlFields";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { useSearchParams } from "@shared/react-router-dom/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { RecaptchaErrorBox } from "@unauthenticated/shared/components/RecaptchaErrorBox/RecaptchaErrorBox";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { UserSendMagicLinkUnauthenticatedSharedMutation as UserSendMagicLinkMutationData } from "@unauthenticated/shared/data/mutations/UserSendMagicLink.mutation.generated";
import { useUserSendMagicLinkUnauthenticatedSharedMutation as useUserSendMagicLinkMutation } from "@unauthenticated/shared/data/mutations/UserSendMagicLink.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneyEmailFormContent.styles";

// URL parameter constants
const URL_PARAMS = {
  STORE_ID: "store_id",
  REDIRECT_PATH: "redirect_path",
  INTAKE: "intake",
  EMAIL: "email",
} as const;

const BOOLEAN_VALUES = {
  TRUE: "true",
} as const;

// Form constants
const FORM_IDS = {
  EMAIL_INPUT: "instant-link-email-input",
} as const;

// Magic link page constants
const MAGIC_LINK_PAGES = {
  EMAIL: "email",
  SUCCESS: "success",
} as const;

// Route path constants
const ROUTE_PATHS = {
  AUTH_CHECKOUT_PREFIX: "/r/checkout/",
  UNAUTH_CHECKOUT_PREFIX: "/checkout/",
  JOURNEY_PATH: "/journey/",
} as const;

type MagicLinkPage = (typeof MAGIC_LINK_PAGES)[keyof typeof MAGIC_LINK_PAGES];

interface JourneyEmailFormContentProps {
  email: string;
  storeId?: string;
  setEmail: (email: string) => void;
  setPage: (page: MagicLinkPage) => void;
  onBackToLogin?: () => void;
  authenticatedRedirectPath?: string;
}

const JourneyEmailFormContent = ({
  email,
  storeId,
  setEmail,
  setPage,
  onBackToLogin,
  authenticatedRedirectPath,
}: JourneyEmailFormContentProps) => {
  const { t } = useTranslation();
  const { emailRegex } = useGlobalConfig();
  const [searchParams] = useSearchParams();
  const utmPayload = useUnauthUrlFields({ isCamelCaseKeys: true });
  const [recaptchaError, setRecaptchaError] = useState(false);
  const redirectPath = searchParams?.get(URL_PARAMS.REDIRECT_PATH);
  const intake = searchParams?.get(URL_PARAMS.INTAKE) === BOOLEAN_VALUES.TRUE;
  const previousEmail = searchParams.get(URL_PARAMS.EMAIL);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!email && previousEmail) {
      setEmail(previousEmail);
    }
  }, [previousEmail, email, setEmail]);

  const onCompleted = (data: UserSendMagicLinkMutationData) => {
    setIsLoading(false);
    const { message: errorMessage } = data?.auth?.userSendMagicLink?.errors ?? {};

    if (!errorMessage) {
      setPage(MAGIC_LINK_PAGES.SUCCESS);
    }
  };

  const [sendMagicLink, { loading, data }] = useUserSendMagicLinkMutation({
    onCompleted,
  });

  const handleSendLink = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    setIsLoading(true);

    gRecaptchaExecute(gToken => {
      sendMagicLink({
        variables: {
          input: {
            captchaToken: gToken,
            email,
            intake,
            ...(storeId && { storeId }),
            redirectPath: authenticatedRedirectPath,
            ...utmPayload,
          },
        },
      });
    }, setRecaptchaError);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const { message: errorMessage } = data?.auth?.userSendMagicLink?.errors ?? {};

  const getTryDifferentSignInLink = () => {
    // Journey checkout redirect logic - convert authenticated to unauthenticated path
    if (
      redirectPath &&
      redirectPath.includes(ROUTE_PATHS.AUTH_CHECKOUT_PREFIX) &&
      redirectPath.includes(ROUTE_PATHS.JOURNEY_PATH)
    ) {
      // Convert from authenticated path /r/checkout/... to unauthenticated path /checkout/...
      // This preserves any state parameters (email, zipCode, nextStep) in the URL
      const unauthenticatedPath = redirectPath.replace(
        ROUTE_PATHS.AUTH_CHECKOUT_PREFIX,
        ROUTE_PATHS.UNAUTH_CHECKOUT_PREFIX
      );

      return unauthenticatedPath;
    }

    return authRoutes.login;
  };

  const handleTryDifferentSignIn = (e: React.MouseEvent) => {
    // If onBackToLogin is provided (journey flow), use it to go back to previous view
    if (onBackToLogin) {
      e.preventDefault();
      onBackToLogin();

      return;
    }

    // Journey checkout redirect logic - convert authenticated to unauthenticated path
    if (
      redirectPath &&
      redirectPath.includes(ROUTE_PATHS.AUTH_CHECKOUT_PREFIX) &&
      redirectPath.includes(ROUTE_PATHS.JOURNEY_PATH)
    ) {
      e.preventDefault();

      // Convert from authenticated path /r/checkout/... to unauthenticated path /checkout/...
      // This preserves any state parameters (email, zipCode, nextStep) in the URL
      const unauthenticatedPath = redirectPath.replace(
        ROUTE_PATHS.AUTH_CHECKOUT_PREFIX,
        ROUTE_PATHS.UNAUTH_CHECKOUT_PREFIX
      );

      // Use browser navigation instead of React Router since it doesn't work in unauthenticated SPA
      window.location.href = unauthenticatedPath;
    }
    // If not journey checkout, let the Link handle normal navigation
  };

  return (
    <>
      {!!errorMessage && (
        <Message
          isColor="danger"
          titleContent={t(l.signIn.continueWithoutPassword.error.title)}
          css={styles.errorMessage}
        >
          {errorMessage}
        </Message>
      )}
      {!!recaptchaError && <RecaptchaErrorBox />}
      <div css={styles.textWrapper}>
        <Typography type="h1">{t(l.signIn.continueWithoutPassword.title)}</Typography>
        <Typography>{t(l.signIn.continueWithoutPassword.description)}</Typography>
        <form onSubmit={handleSendLink}>
          <FloatingLabelInput
            id={FORM_IDS.EMAIL_INPUT}
            type="email"
            label={t(l.common.Email)}
            value={email}
            handleChange={handleEmailChange}
            required
            wrapperStyles={styles.emailInput}
          />
          <Arrange spacing="xlarge" isVertical alignment="center">
            <Button
              isLoading={isLoading || loading}
              type="submit"
              disabled={isLoading || loading || !isEmailValid(email, emailRegex)}
            >
              {t(l.signIn.continueWithoutPassword.cta)}
            </Button>
            <Link
              to={getTryDifferentSignInLink()}
              onClick={handleTryDifferentSignIn}
              isColor="system"
              isUnderLined={false}
            >
              <FontAwesomeIcon icon={faChevronLeft} aria-hidden css={styles.chevron} />
              {t(l.signIn.continueWithoutPassword.tryDifferentSignIn)}
            </Link>
          </Arrange>
        </form>
      </div>
    </>
  );
};

export { JourneyEmailFormContent };
